import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Label } from '@eneco/dc/client/types';
import { DC_Repositories_Base_Enumerations_BusinessUnit } from '@monorepo-types/dc';
import { useMachine } from '@xstate/react';

import { FlowContext } from '../types/machine';
import { useFlowHooks } from '../utils/FlowProvider';

export interface OfferFlowContext<TOffer> extends FlowContext {
  offer?: TOffer;
  offerRequestHash?: string;
}

export type GetOfferEvent<TFlowContext> = { type: 'GET_OFFER'; flowContext: TFlowContext };

export interface CreateOfferMachineHooksOptions<TOffer, TEvent> {
  /** Factory that builds a fresh getOffer machine instance, optionally seeded with a cached offer. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOfferMachine: (fetchedOffers?: TOffer) => any;
  /** Builds a stable fingerprint of a GET_OFFER-like event's inputs, used to detect cache hits. */
  getOfferEventHash: (event: TEvent) => string;
}

/**
 * Builds a pair of `useOfferMachine`/`useCachedOfferMachine` hooks for a flow's getOffer machine.
 * Extracted so flows with an equivalent getOffer machine (offer fetch + flow-context caching) don't
 * duplicate the caching/write-back logic — only the machine and hash function differ per flow.
 */
export const createOfferMachineHooks = <TOffer, TFlowContext extends OfferFlowContext<TOffer>, TEvent>({
  getOfferMachine,
  getOfferEventHash,
}: CreateOfferMachineHooksOptions<TOffer, TEvent>) => {
  const useOfferMachine = (
    businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit,
    label: Label,
    fetchedOffers?: TOffer,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): any => {
    const machine = useMemo(() => getOfferMachine(fetchedOffers), [fetchedOffers]);

    const [offerState, sendToOfferMachine] = useMachine(machine, {
      input: { businessUnit, label },
    });

    return { offerState, sendToOfferMachine };
  };

  /**
   * Flow-aware wrapper around {@link useOfferMachine}. It reuses the offer response cached in the flow
   * context when the request inputs are unchanged, and persists every freshly fetched response back to
   * the flow context. This deduplicates the offer-products call across adjacent steps and back/forward
   * navigation, while still refetching whenever any input affecting the offer changes.
   *
   * Must be rendered within a FlowProvider. The plain {@link useOfferMachine} remains available for
   * standalone usage that has no flow context.
   */
  const useCachedOfferMachine = (
    businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit,
    label: Label,
    fetchedOffers?: TOffer,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): any => {
    const flowActor = useFlowHooks<TFlowContext>().useFlowActorRef();

    // Seed the machine with the offer cached in the flow context, captured once at mount so a later
    // write-back (which mutates the flow context) doesn't recreate and reset the machine.
    const seededOfferRef = useRef(fetchedOffers ?? flowActor.getSnapshot().context.offer);

    const { offerState, sendToOfferMachine: rawSend } = useOfferMachine(businessUnit, label, seededOfferRef.current);

    // Tracks the fingerprint of an in-progress fetch so its result can be written back on success.
    const pendingHashRef = useRef<string | undefined>(undefined);

    const sendToOfferMachine = useCallback(
      (event: TEvent) => {
        const { offer: cachedOffer, offerRequestHash: cachedHash } = flowActor.getSnapshot().context;
        const hash = getOfferEventHash(event);

        if (cachedOffer && cachedHash && cachedHash === hash) {
          rawSend({ type: 'CACHE_HIT' });
          return;
        }

        pendingHashRef.current = hash;
        rawSend(event);
      },
      [flowActor, rawSend],
    );

    useEffect(() => {
      if (pendingHashRef.current && offerState.matches('SUCCESS') && offerState.context.offer) {
        flowActor.send({
          type: 'UPDATE_VALUES',
          values: { offer: offerState.context.offer, offerRequestHash: pendingHashRef.current } as TFlowContext,
        });
        pendingHashRef.current = undefined;
      }
    }, [offerState, flowActor]);

    return { offerState, sendToOfferMachine };
  };

  return { useOfferMachine, useCachedOfferMachine };
};
